/* eslint-disable no-undef */
import React, { useEffect } from 'react';
import { setCookie, destroyCookie } from 'utils/cookies';
import queryString from 'utils/queryString';
import PropTypes from 'prop-types';
import ScriptLoader from 'react-script-loader-hoc';

import SETTINGS from '../settings';

const AMAZON_COOKIE_KEY = 'amazon_Login_accessToken';

const withAmazon = Component => {
  const WrappedComponent = ({ scriptsLoaded, scriptsLoadedSuccessfully, ...props }) => {
    useEffect(() => {
      if (scriptsLoadedSuccessfully) {
        window.onAmazonLoginReady = () => {
          amazon.Login.setClientId(SETTINGS.AMAZON_PAY_CLIENT_ID);
          amazon.Login.setUseCookie(true);
        };
      }
    }, [scriptsLoadedSuccessfully]);

    return <Component {...props} />;
  };

  WrappedComponent.getInitialProps = async ctx => {
    if (Component.getInitialProps) {
      return Component.getInitialProps(ctx);
    }

    return {};
  };

  WrappedComponent.propTypes = {
    scriptsLoaded: PropTypes.bool,
    scriptsLoadedSuccessfully: PropTypes.bool,
  };

  return ScriptLoader(SETTINGS.AMAZON_PAY_SCRIPT_URL)(WrappedComponent);
};

const getAccessToken = () => {
  const parsedHash = queryString.parse(window.location.hash);

  if (parsedHash.access_token) {
    const accessToken = parsedHash.access_token;
    if (accessToken.length > 0) {
      if (accessToken.match(/^Atza/)) {
        window.history.replaceState('', '', window.location.pathname);
        return accessToken;
      }
    }
  }

  return null;
};

const retriveAmazonProfile = (accessToken, callback) => {
  amazon.Login.retrieveProfile(accessToken, response => {
    callback(response);
  });
};

export const amazonLogout = () => {
  try {
    amazon.Login.logout();
  } catch (e) {
    console.log(e);
  }
  destroyCookie(null, AMAZON_COOKIE_KEY);
};

export const prepareAmazonLogin = (
  parentElementId = 'amazon-login-button',
  redirectUrl = '/',
  onLoading,
  onSuccess,
  onFailed,
) => {
  const accessToken = getAccessToken();

  if (accessToken) {
    setCookie(null, AMAZON_COOKIE_KEY, accessToken);
    onLoading(accessToken);
  }

  const handleOnAmazonPaymentsReady = (repetition = 6) => {
    if (repetition === 0) {
      if (document.getElementById(parentElementId)) {
        onFailed('unavailable');
      } else {
        console.log(`Element #${parentElementId} did not found.`);
      }
      return;
    }

    if (window.OffAmazonPayments && document.getElementById(parentElementId)) {
      if (accessToken) {
        const getProfilePromise = () =>
          new Promise((resolve, reject) => {
            retriveAmazonProfile(accessToken, data => {
              if (data.success) {
                resolve(data.profile);
                return;
              }
              reject(data);
            });
          });

        onSuccess(accessToken, getProfilePromise);
      }

      OffAmazonPayments.Button(parentElementId, SETTINGS.AMAZON_PAY_SELLER_ID, {
        type: 'LwA',
        color: 'Gold',
        size: 'medium',

        authorization: () => {
          amazon.Login.authorize({ scope: 'profile', popup: false }, redirectUrl);
        },

        onError: error => {
          onFailed(error.getErrorMessage());
        },
      });
    } else {
      setTimeout(() => handleOnAmazonPaymentsReady(repetition - 1), 500);
    }
  };

  handleOnAmazonPaymentsReady();
};

export const prepareAmazonPay = (
  parentElementId = 'amazon-pay-button',
  redirectUrl = '/',
  onLoading,
  onSuccess,
  onFailed,
) => {
  const accessToken = getAccessToken();

  if (accessToken) {
    setCookie(null, AMAZON_COOKIE_KEY, accessToken);
    onLoading(accessToken);
  }

  const handleOnAmazonPaymentsReady = (repetition = 6) => {
    if (repetition === 0) {
      if (document.getElementById(parentElementId)) {
        onFailed('unavailable');
      } else {
        console.log(`Element #${parentElementId} did not found.`);
      }
      return;
    }

    if (window.OffAmazonPayments && document.getElementById(parentElementId)) {
      if (accessToken) {
        const getProfilePromise = () =>
          new Promise((resolve, reject) => {
            retriveAmazonProfile(accessToken, data => {
              if (data.success) {
                resolve(data.profile);
                return;
              }
              reject(data);
            });
          });

        onSuccess(accessToken, getProfilePromise);
      }

      OffAmazonPayments.Button(parentElementId, SETTINGS.AMAZON_PAY_SELLER_ID, {
        type: 'PwA',
        color: 'Gold',
        size: 'medium',

        authorization: () => {
          amazon.Login.authorize({ scope: 'profile payments:widget', popup: false }, redirectUrl);
        },

        onError: error => {
          onFailed(error.getErrorMessage());
        },
      });
    } else {
      setTimeout(() => handleOnAmazonPaymentsReady(repetition - 1), 500);
    }
  };

  handleOnAmazonPaymentsReady();
};

export const AmazonPayAddressBookWidget = props => {
  const handleOnAmazonPaymentsReady = (repetition = 6) => {
    if (repetition === 0) {
      props.onError('AmazonPayScriptError', 'Amazon Pay is temporarily unavailable.');
      return;
    }

    if (window.OffAmazonPayments) {
      new OffAmazonPayments.Widgets.AddressBook({
        sellerId: SETTINGS.AMAZON_PAY_SELLER_ID,
        design: {
          designMode: 'responsive',
        },
        onOrderReferenceCreate: orderReference => {
          props.onReceiveReferenceId(orderReference.getAmazonOrderReferenceId());
        },
        onAddressSelect: orderReference => {
          props.onSelect();
        },
        onReady: orderReference => {},
        onError: error => {
          props.onError(error.getErrorCode(), error.getErrorMessage());
        },
      }).bind('addressBookWidgetDiv');
    } else {
      setTimeout(() => handleOnAmazonPaymentsReady(repetition - 1), 500);
    }
  };

  useEffect(() => {
    handleOnAmazonPaymentsReady();
  }, []);

  return <div id="addressBookWidgetDiv" className={props.className} style={{ height: 260 }} />;
};

AmazonPayAddressBookWidget.propTypes = {
  onReceiveReferenceId: PropTypes.func,
  onSelect: PropTypes.func,
  onError: PropTypes.func,
  className: PropTypes.string,
};

export const AmazonPayWalletWidget = props => {
  const handleOnAmazonPaymentsReady = (repetition = 6) => {
    if (repetition === 0) {
      props.onError('AmazonPayScriptError', 'Amazon Pay is temporarily unavailable.');
      return;
    }

    if (window.OffAmazonPayments) {
      new OffAmazonPayments.Widgets.Wallet({
        sellerId: SETTINGS.AMAZON_PAY_SELLER_ID,
        design: {
          designMode: 'responsive',
        },
        onPaymentSelect: orderReference => {
          props.onSelect();
        },
        onError: error => {
          props.onError(error.getErrorCode(), error.getErrorMessage());
        },
      }).bind('walletWidgetDiv');
    } else {
      setTimeout(() => handleOnAmazonPaymentsReady(repetition - 1), 500);
    }
  };

  useEffect(() => {
    handleOnAmazonPaymentsReady();
  }, []);

  return <div id="walletWidgetDiv" className={props.className} style={{ height: 260 }} />;
};

AmazonPayWalletWidget.propTypes = {
  onSelect: PropTypes.func,
  onError: PropTypes.func,
  className: PropTypes.string,
};

export default withAmazon;
